import { parse } from 'qs';
import { showElements, hideElements } from 'client/javascript/util/dom_utils';
import { SPECIAL_CAPACITIES_MAP } from 'shared/util/search_constants';
import User, { USER_STORAGE_KEY_MAP } from 'shared/storage/user';
import { ISODateToLocalizedShortDate } from 'client/javascript/util/locale';
import { trackFilterClick, trackFilterApplyClick, trackFilterClearClick } from './analytics';
import initFlatpickr from './date_picker';

export default class SearchFilters {
  constructor(options) {
    const queryString = window.location.search;
    const queryParams = parse(queryString, { ignoreQueryPrefix: true });

    this.onSelect = options.onSelect;
    this.onClear = options.onClear;
    this.onChange = options.onChange; // run in both, onSelect & onClear
    this.defaults = options.defaults || {};
    this.datePickerConfig = options.datePickerConfig || {};
    this.capacity = {
      open: false,
      value: null,
      element: document.querySelector('#search-chip__capacity'),
      dropdown: document.querySelector('#search-dropdown__capacity'),
      buttons: Array.from(document.querySelectorAll('.capacity-chip')),
      clear: document.querySelector('#search-clear__capacity'),
      onePersonCallout: document.querySelector('#capacityFilterOnePersonCallout'),
      text: 'Capacity',
      localizedText: null
    };
    this.date = {
      open: false,
      value: null,
      element: document.getElementById('search-chip__date'),
      input: document.getElementById('filter-chips__select-date'),
      wrapper: document.getElementById('search-chip-wrapper__date'),
      clear: null, // will get initialized later
      text: 'Move-in',
      localizedText: null
    };
    this.workspace = {
      open: false,
      value: null,
      element: document.querySelector('#search-chip__workspace'),
      dropdown: document.querySelector('#search-dropdown__workspace'),
      clear: document.querySelector('#search-clear__workspace'),
      text: 'Workspace Type',
      localizedText: null,
      isWorkspace: true
    };
    this.initializeLocalizedText();
    this.initializeCapacityEvents();
    this.initializeCapacityValue(queryParams);
    this.initializeClearAllFilters();
    this.initializeDate();
    this.initializeDateValue(queryParams);
    this.initializeWorkspaceFilter(queryParams);
  }

  get filters() {
    return [this.capacity, this.date, this.workspace];
  }

  get values() {
    return {
      capacity: this.capacity.value || this.defaults.capacity,
      date: this.date.value || this.defaults.date,
      workspace: this.workspace.value
    };
  }

  initializeLocalizedText() {
    const capacitySpan = this.capacity.element.querySelector('span');
    this.capacity.localizedText = capacitySpan.textContent;
    const dateSpan = this.date.element.querySelector('span');
    this.date.localizedText = dateSpan.textContent;
    if (this.workspace.element) {
      const workspaceSpan = this.workspace.element.querySelector('span');
      this.workspace.localizedText = workspaceSpan.textContent;
    }
  }

  initializeWorkspaceFilter(queryParams) {
    if (!this.workspace.element) return;
    this.initializeWorkspaceValue(queryParams);
    this.workspace.element.addEventListener('click', () => this.handleFilterClick(this.workspace));
    this.workspace.dropdown.addEventListener('click', event => this.handleWorkspaceSelect(event));
    this.workspace.clear.addEventListener('click', () => this.handleWorkspaceClearClick());
  }

  initializeWorkspaceValue(queryParams) {
    const workspaceValue = queryParams.workspace || User.getValue(USER_STORAGE_KEY_MAP.workspace);

    if (workspaceValue) {
      this.workspace.value = workspaceValue;
      SearchFilters.updateFilter(this.workspace);
      const radioContainer = this.workspace.dropdown.querySelector(`div.ray-radio[value="${workspaceValue}"]`);
      if (radioContainer) radioContainer.classList.add('checked');
      this.updateCapacityBasedOnWorkpace();
    }
  }

  initializeCapacityEvents() {
    this.capacity.element.addEventListener('click', () => this.handleFilterClick(this.capacity));
    this.capacity.clear.addEventListener('click', () => this.handleCapacityClearClick());
    this.capacity.dropdown.addEventListener('click', event => this.handleCapacitySelect(event));
  }

  initializeCapacityValue(queryParams) {
    const capacity = queryParams.capacity || User.getValue(USER_STORAGE_KEY_MAP.capacity);
    const capacityButton =
      capacity &&
      this.capacity.buttons.find(
        button => capacity === (SPECIAL_CAPACITIES_MAP[button.textContent] || button.textContent)
      );
    if (capacityButton) {
      capacityButton.classList.add('ray-chip--active'); // highlight capacity button inside dropdown
      this.capacity.value = capacityButton.textContent;
      SearchFilters.updateFilter(this.capacity);
    }
  }

  initializeClearAllFilters() {
    const clearAllButton = document.querySelector('#no-filter-results a');
    const clearWorkspaceFilters = document.getElementById('clear-workspace-selection-filters');

    if (clearAllButton) {
      clearAllButton.addEventListener('click', () => {
        this.handleCapacityClearClick();
        this.handleDateClearClick();
      });
    }
    if (clearWorkspaceFilters) {
      clearWorkspaceFilters.addEventListener('click', () => {
        this.handleCapacityClearClick();
        this.handleDateClearClick();
      });
    }
  }

  async initializeDate() {
    this.date.datePicker = await initFlatpickr({
      input: this.date.input,
      onChange: value => this.handleDateChange(value),
      appendTo: this.date.wrapper,
      ...this.datePickerConfig
    });

    this.date.datePicker.calendarContainer.classList.add('filter-chips__chip-dropdown');
    this.date.clear = document.getElementById('search-clear__date');
    this.date.element.addEventListener('click', () => this.handleFilterClick(this.date));
    this.date.clear.addEventListener('click', () => this.handleDateClearClick());
    this.date.value = this.date.input.value; // Update with stored Flatpickr value
    SearchFilters.updateFilter(this.date);
  }

  initializeDateValue(queryParams) {
    const defaultDate =
      queryParams.date || queryParams.start_date || User.getValue(USER_STORAGE_KEY_MAP.startDateFilter);
    if (defaultDate && defaultDate.match(/[0-9]{4}-[0-9]{2}-[0-9]{2}/)) {
      this.date.value = defaultDate;
    }
  }

  handleFilterClick(filter) {
    const { open } = filter;
    this.closeAllFilters();
    if (!open) {
      this.openFilter(filter);
    }
  }

  handleCapacityClearClick() {
    this.handleFilterClear(this.capacity);
    User.clearValue(USER_STORAGE_KEY_MAP.capacity);
    this.capacity.buttons.forEach(button => {
      button.classList.remove('ray-chip--active');
    });
  }

  handleWorkspaceClearClick() {
    const checkedRadioContainer = this.workspace.dropdown.querySelector('.checked');
    checkedRadioContainer.classList.remove('checked');
    this.handleFilterClear(this.workspace);
    User.clearValue(USER_STORAGE_KEY_MAP.workspace);
    this.enableCapacityButtons();
  }

  handleDateClearClick() {
    this.handleFilterClear(this.date);
    User.clearValue(USER_STORAGE_KEY_MAP.startDateFilter);
    this.date.datePicker.clear();
  }

  handleFilterClear(filter) {
    filter.value = null; // eslint-disable-line no-param-reassign
    filter.element.classList.remove('filter-selected');
    hideElements(filter.clear);
    SearchFilters.updateFilter(filter);
    this.closeFilter(filter);
    trackFilterClearClick(filter);
    if (this.onClear) this.onClear(filter);
    if (this.onChange) this.onChange(filter);
  }

  handleFilterSelect(filter) {
    SearchFilters.updateFilter(filter);
    trackFilterApplyClick(filter);
    if (this.onSelect) this.onSelect(filter);
    if (this.onChange) this.onChange(filter);
  }

  handleDateChange(value) {
    if (value.length) {
      const dateValue = this.date.input.value;
      this.date.value = dateValue;
      User.saveValue(USER_STORAGE_KEY_MAP.startDateFilter, dateValue);
      this.date.open = false; // eslint-disable-line no-param-reassign
      this.handleFilterSelect(this.date);
    }
  }

  handleCapacitySelect(event) {
    const element = event.target;
    if (!element.classList.contains('capacity-chip') || element.classList.contains('ray-chip--disabled')) {
      element.classList.remove('ray-chip--active'); // added by ray-js even the chip is disabled
      return;
    }

    const capacity = element.textContent;
    this.capacity.buttons.forEach(button => {
      if (button !== element) {
        button.classList.remove('ray-chip--active');
      }
    });
    element.classList.add('ray-chip--active'); // Add again in case user clicked the selected chip
    this.capacity.value = capacity;
    User.saveValue(USER_STORAGE_KEY_MAP.capacity, SPECIAL_CAPACITIES_MAP[capacity] || capacity);
    this.closeFilter(this.capacity);
    this.handleFilterSelect(this.capacity);
  }

  handleWorkspaceSelect(event) {
    const radioContainer = event.target.closest('.ray-radio');
    if (!radioContainer) {
      return;
    }
    this.workspace.dropdown.querySelectorAll('.checked').forEach(i => i.classList.remove('checked'));
    this.workspace.value = radioContainer.getAttribute('value');
    radioContainer.classList.add('checked');
    User.saveValue(USER_STORAGE_KEY_MAP.workspace, this.workspace.value);
    this.closeFilter(this.workspace);
    this.updateCapacityBasedOnWorkpace();
    this.handleFilterSelect(this.workspace);
  }

  enableCapacityButtons() {
    this.capacity.dropdown
      .querySelectorAll('.capacity-chip')
      .forEach(chip => chip.classList.remove('ray-chip--disabled'));
    hideElements(this.capacity.onePersonCallout);
  }

  disableCapacityButtons() {
    if (this.capacity.value !== '1') this.capacity.dropdown.querySelector('.capacity-chip').click();
    this.capacity.dropdown
      .querySelectorAll('.capacity-chip:not(.ray-chip--active)')
      .forEach(chip => chip.classList.add('ray-chip--disabled'));
    showElements(this.capacity.onePersonCallout);
  }

  updateCapacityBasedOnWorkpace() {
    if (this.workspace.value === 'privateOffice') {
      this.enableCapacityButtons();
    } else {
      this.disableCapacityButtons();
    }
  }

  closeFilterDropdown = event => {
    const clickedOutsideDropdown = !event.target.closest('.filter-chips__chip-dropdown');

    if (clickedOutsideDropdown) {
      this.closeAllFilters();
    }
  };

  openFilter(filter) {
    filter.open = true; // eslint-disable-line no-param-reassign
    filter.element.classList.add('active');
    this.openDropdown(filter);
    this.closeFilterListener = document.addEventListener('mouseup', this.closeFilterDropdown);
    trackFilterClick(filter);
  }

  openDropdown(filter) {
    if (filter.datePicker) {
      this.date.datePicker.open();
    } else {
      filter.dropdown.classList.remove('hidden');
      setTimeout(() => {
        filter.dropdown.classList.add('active');
      }, 20); // Need to slightly delay this class addition to get CSS transition to display
    }
  }

  closeAllFilters() {
    this.filters.forEach(filter => this.closeFilter(filter));
  }

  closeFilter(filter) {
    if (!filter.open) return;
    filter.open = false; // eslint-disable-line no-param-reassign
    document.removeEventListener('mousedown', this.closeFilterDropdown);
    filter.element.classList.remove('ray-chip--active');
    filter.element.classList.remove('active');
    this.closeDropdown(filter);
  }

  closeDropdown(filter) {
    if (filter.datePicker) {
      this.date.datePicker.close();
    } else {
      filter.dropdown.classList.remove('active');
      filter.dropdown.classList.add('hidden');
    }
  }

  static updateFilter(filter) {
    let text;
    if (filter.datePicker && filter.value) {
      text = ISODateToLocalizedShortDate(filter.value);
    } else if (filter.isWorkspace) {
      const textFromTitle = filter.dropdown.querySelector(`#${filter.value}`);
      if (textFromTitle) text = textFromTitle.textContent;
    } else {
      text = filter.value;
    }
    const img = filter.element.querySelector('img');
    const span = filter.element.querySelector('span');
    if (text) {
      span.innerText = ` ${text}`;
      showElements(img, filter.clear);
      filter.element.classList.add('filter-selected');
    } else {
      span.innerText = filter.localizedText;
      hideElements(img, filter.clear);
    }
  }
}
